import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, ExternalLink, PackageCheck } from 'lucide-react';
import StoreLayout from '../../layouts/StoreLayout';
import { api } from '../../lib/api';
import { formatPrice } from '../../lib/mockData';
import { cacheStoreBranding, deriveStoreBranding, getCachedStoreBranding } from '../../lib/storeBranding';

const orderStatusLabels: Record<string, string> = {
    AWAITING_PAYMENT: 'Aguardando pagamento',
    PENDING: 'Aguardando preparo',
    CONFIRMED: 'Confirmado',
    PREPARING: 'Em preparo',
    READY: 'Pronto',
    DELIVERED: 'Entregue',
    CANCELED: 'Cancelado',
};

const paymentStatusLabels: Record<string, string> = {
    PENDING: 'Pagamento pendente',
    PAID: 'Pago',
    FAILED: 'Falhou',
    EXPIRED: 'Expirado',
};

const orderTypeLabels: Record<string, string> = {
    LOCAL: 'Mesa',
    PICKUP: 'Retirada',
    DELIVERY: 'Delivery',
};

const orderTimeline = ['AWAITING_PAYMENT', 'CONFIRMED', 'PREPARING', 'READY', 'DELIVERED'];

const asArray = <T,>(value: T[] | null | undefined): T[] => Array.isArray(value) ? value : [];

const StoreOrderStatusPage: React.FC = () => {
    const { slug, shortId } = useParams<{ slug: string; shortId: string }>();
    const [cachedBranding, setCachedBranding] = useState(() => slug ? getCachedStoreBranding(slug) : null);
    const [establishment, setEstablishment] = useState<any>(null);
    const [order, setOrder] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!slug || !shortId) return;

        let active = true;

        const load = async () => {
            try {
                const establishmentRes = await api.get(`/establishments/${slug}`);
                if (!active) return;
                setEstablishment(establishmentRes.data);
                const nextBranding = deriveStoreBranding(establishmentRes.data);
                cacheStoreBranding(slug, nextBranding);
                setCachedBranding(nextBranding);

                const orderRes = await api.get(`/orders/public/establishment/${establishmentRes.data.id}/track/${shortId}`);
                if (!active) return;
                setOrder(orderRes.data);
                setError('');
            } catch (requestError) {
                console.error('Failed to load public order status', requestError);
                if (active) {
                    setError('Nao foi possivel localizar este pedido.');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        load();
        const interval = window.setInterval(load, 15000);

        return () => {
            active = false;
            window.clearInterval(interval);
        };
    }, [shortId, slug]);

    useEffect(() => {
        if (!slug) return;
        setCachedBranding(getCachedStoreBranding(slug));
    }, [slug]);

    const paymentMethodLabel = useMemo(() => {
        if (!order?.paymentMethod) return null;

        if (order.paymentMethod === 'MERCADO_PAGO') return 'Mercado Pago';
        if (order.paymentMethod === 'CARD_ON_DELIVERY') return 'Cartao na entrega';
        if (order.paymentMethod === 'CASH') return 'Dinheiro';
        return order.paymentMethod;
    }, [order?.paymentMethod]);

    const orderItemsSubtotal = useMemo(
        () => asArray(order?.items).reduce((sum: number, item: any) => sum + item.price * item.quantity, 0),
        [order?.items],
    );

    const storeBranding = useMemo(
        () => (establishment ? deriveStoreBranding(establishment) : cachedBranding),
        [cachedBranding, establishment],
    );
    const brandPrimary = storeBranding?.primaryColor || '#ff6b00';
    const brandAccent = storeBranding?.accentColor || '#ffb26b';
    const brandSoftBorder = `${brandPrimary}33`;
    const brandSoftBackground = `${brandPrimary}14`;
    const currentTimelineIndex = order?.status
        ? orderTimeline.indexOf(order.status)
        : -1;

    return (
        <StoreLayout
            slug={slug || ''}
            establishmentName={establishment?.name || cachedBranding?.name}
            logoUrl={establishment?.logoUrl || cachedBranding?.logoUrl}
            coverImageUrl={storeBranding?.coverImageUrl}
            tagline={storeBranding?.tagline}
            primaryColor={storeBranding?.primaryColor}
            accentColor={storeBranding?.accentColor}
            themePreset={storeBranding?.themePreset}
            showHero={false}
        >
            <div className="space-y-6">
                <Link
                    to={`/store/${slug}`}
                    className="inline-flex items-center gap-2 rounded-xl border bg-[#111] px-4 py-2 text-sm font-semibold text-white transition"
                    style={{ borderColor: brandSoftBorder }}
                >
                    <ArrowLeft className="h-4 w-4" />
                    Voltar ao cardapio
                </Link>

                {loading ? (
                    <div className="rounded-3xl border border-[#2A2A2A] bg-[#111] p-8 text-center text-muted-foreground">
                        Carregando status do pedido...
                    </div>
                ) : error || !order ? (
                    <div className="rounded-3xl border border-[#2A2A2A] bg-[#111] p-8 text-center">
                        <PackageCheck className="mx-auto h-10 w-10 text-muted-foreground opacity-50" />
                        <p className="mt-4 text-lg font-bold text-white">{error || 'Pedido nao encontrado'}</p>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Confira o codigo informado ou volte para o cardapio para consultar outro pedido.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <div className="rounded-3xl border bg-[#111] p-6" style={{ borderColor: brandSoftBorder, boxShadow: `0 24px 60px ${brandSoftBackground}` }}>
                            <p className="text-xs uppercase tracking-[0.24em]" style={{ color: brandAccent }}>Pedido {order.shortId}</p>
                            <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                                <div>
                                    <h1 className="text-3xl font-black text-white">
                                        {orderStatusLabels[order.status] || order.status}
                                    </h1>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        Atualiza automaticamente a cada 15 segundos.
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <span className="rounded-full border border-[#2A2A2A] bg-[#181818] px-3 py-1 text-xs font-semibold text-white">
                                        {orderTypeLabels[order.type] || order.type}
                                    </span>
                                    {paymentMethodLabel && (
                                        <span className="rounded-full bg-[#1A1A1A] px-3 py-1 text-xs text-muted-foreground">
                                            {paymentMethodLabel}
                                        </span>
                                    )}
                                    {order.paymentStatus && (
                                        <span className="rounded-full px-3 py-1 text-xs" style={{ backgroundColor: brandSoftBackground, color: brandPrimary }}>
                                            {paymentStatusLabels[order.paymentStatus] || order.paymentStatus}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {!['CANCELED'].includes(order.status) && (
                                <div className="mt-5 rounded-2xl border border-white/8 bg-black/20 p-4">
                                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/55">
                                        Andamento do pedido
                                    </p>
                                    <div className="mt-3 grid grid-cols-5 gap-2">
                                        {orderTimeline.map((status, index) => {
                                            const isDone = currentTimelineIndex >= index || (order.status === 'DELIVERED' && index <= 4);
                                            return (
                                                <div
                                                    key={status}
                                                    className="rounded-xl border px-2 py-3 text-center"
                                                    style={{
                                                        borderColor: isDone ? brandSoftBorder : '#2A2A2A',
                                                        backgroundColor: isDone ? brandSoftBackground : '#121212',
                                                        color: isDone ? brandPrimary : '#a3a3a3',
                                                    }}
                                                >
                                                    <div className="text-[10px] font-black">{index + 1}</div>
                                                    <div className="mt-1 text-[11px] font-semibold leading-4">
                                                        {orderStatusLabels[status]}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid gap-6 lg:grid-cols-[1.4fr_0.8fr]">
                            <div className="rounded-3xl border border-[#2A2A2A] bg-[#111] p-6">
                                <h2 className="text-lg font-bold text-white">Itens do pedido</h2>
                                <div className="mt-4 space-y-3">
                                    {asArray(order.items).map((item: any) => (
                                        <div key={item.id} className="flex items-start justify-between gap-3 rounded-2xl border border-[#202020] bg-[#0C0C0C] p-4">
                                            <div>
                                                <p className="font-semibold text-white">
                                                    {item.quantity}x {item.product?.name || 'Item'}
                                                </p>
                                                {item.notes && (
                                                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.notes}</p>
                                                )}
                                            </div>
                                            <div className="text-sm font-bold text-white">
                                                {formatPrice(item.price * item.quantity)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="rounded-3xl border border-[#2A2A2A] bg-[#111] p-6">
                                    <h2 className="text-lg font-bold text-white">Resumo</h2>
                                    <div className="mt-4 space-y-3 text-sm">
                                        <div className="flex items-center justify-between text-muted-foreground">
                                            <span>Cliente</span>
                                            <span className="font-semibold text-white">{order.customerName || 'Nao informado'}</span>
                                        </div>
                                        {order.customerPhone && (
                                            <div className="flex items-center justify-between text-muted-foreground">
                                                <span>Telefone</span>
                                                <span className="font-semibold text-white">{order.customerPhone}</span>
                                            </div>
                                        )}
                                        {order.deliveryAddress && (
                                            <div className="space-y-1 text-muted-foreground">
                                                <p>Endereco</p>
                                                <p className="font-semibold text-white">{order.deliveryAddress}</p>
                                            </div>
                                        )}
                                        {order.type === 'DELIVERY' && (
                                            <>
                                                <div className="flex items-center justify-between text-muted-foreground">
                                                    <span>Subtotal dos itens</span>
                                                    <span className="font-semibold text-white">{formatPrice(orderItemsSubtotal)}</span>
                                                </div>
                                                <div className="space-y-1 text-muted-foreground">
                                                    <div className="flex items-center justify-between">
                                                        <span>Taxa de entrega</span>
                                                        <span className="font-semibold text-white">{formatPrice(order.deliveryFee || 0)}</span>
                                                    </div>
                                                    <p className="text-xs">Valor cobrado pelo estabelecimento para o delivery.</p>
                                                </div>
                                            </>
                                        )}
                                        <div className="flex items-center justify-between border-t border-[#222] pt-3 text-muted-foreground">
                                            <span>Total</span>
                                            <span className="text-xl font-black" style={{ color: brandPrimary }}>{formatPrice(order.total)}</span>
                                        </div>
                                    </div>
                                </div>

                                {order.status === 'AWAITING_PAYMENT' && (order.pixCopyPasteCode || order.mercadoPagoCheckoutUrl) && (
                                    <div className="rounded-3xl border p-6" style={{ borderColor: brandSoftBorder, backgroundColor: brandSoftBackground }}>
                                        <p className="text-sm text-white/85">
                                            Este pedido ainda esta aguardando o pagamento Pix para ser confirmado.
                                        </p>
                                        {order.pixQrCodeDataUrl && (
                                            <img
                                                src={order.pixQrCodeDataUrl}
                                                alt="QR Code Pix"
                                                className="mt-4 h-44 w-44 rounded-xl bg-white p-2"
                                            />
                                        )}
                                        {order.pixCopyPasteCode && (
                                            <div className="mt-4 rounded-2xl border border-[#2A2A2A] bg-[#111] p-4">
                                                <p className="text-xs uppercase tracking-[0.22em]" style={{ color: brandAccent }}>Pix Copia e Cola</p>
                                                <p className="mt-2 break-all text-sm text-white/85">{order.pixCopyPasteCode}</p>
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        try {
                                                            await navigator.clipboard.writeText(order.pixCopyPasteCode);
                                                        } catch (error) {
                                                            console.error('Failed to copy Pix code', error);
                                                        }
                                                    }}
                                                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold"
                                                    style={{ color: brandPrimary }}
                                                >
                                                    <Copy className="h-4 w-4" />
                                                    Copiar Pix
                                                </button>
                                            </div>
                                        )}
                                        {order.pixExpiresAt && (
                                            <p className="mt-4 text-xs text-white/60">
                                                Valido ate {new Date(order.pixExpiresAt).toLocaleString('pt-BR')}
                                            </p>
                                        )}
                                        {order.mercadoPagoCheckoutUrl && (
                                            <a
                                                href={order.mercadoPagoCheckoutUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold"
                                                style={{ color: brandPrimary }}
                                            >
                                                Abrir pagina do Pix no Mercado Pago <ExternalLink className="h-4 w-4" />
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </StoreLayout>
    );
};

export default StoreOrderStatusPage;
